import React, { useState, useEffect, useCallback } from 'react';
import {
  collection, query, orderBy, getDocs, limit,
  doc, updateDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Search, Phone, MessageSquare, Building2, User, Loader2, UserPlus, Mail, Briefcase, Pencil, X, Check, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { COMM_HUB_T } from '@/constants/communication.constants';
 
interface MarketingContact {
  id: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  email: string | null;
  company_name: string | null;
  role: string;
  conversion_status: string;
  specialty: string | null;
}
 
const CONVERSION_STATUSES = ['contacted', 'interested', 'meeting_scheduled', 'converted', 'not_interested', 'follow_up'];
const STATUS_COLORS: Record<string, string> = {
  contacted: 'bg-blue-500/20 text-blue-400', interested: 'bg-amber-500/20 text-amber-400',
  meeting_scheduled: 'bg-purple-500/20 text-purple-400', converted: 'bg-emerald-500/20 text-emerald-400',
  not_interested: 'bg-red-500/20 text-red-400', follow_up: 'bg-cyan-500/20 text-cyan-400',
};
 
function getStatusLabel(status: string, t: Record<string, string>): string {
  const map: Record<string, string> = {
    contacted: t.statusContacted, interested: t.statusInterested,
    meeting_scheduled: t.statusMeetingScheduled, converted: t.statusConverted,
    not_interested: t.statusNotInterested, follow_up: t.statusFollowUp,
  };
  return map[status] || status;
}
 
// Normalise les champs Firestore (camelCase) → interface attendue
function normalizeContact(id: string, data: any): MarketingContact {
  return {
    id,
    first_name: data.firstName || data.first_name || '',
    last_name: data.lastName || data.last_name || '',
    phone: data.phone || null,
    email: data.email || null,
    company_name: data.companyName || data.company_name || null,
    role: data.role || '',
    conversion_status: data.conversionStatus || data.conversion_status || 'contacted',
    specialty: data.specialty || null,
  };
}
 
interface ContactsDirectoryProps {
  onCall: (phone: string) => void;
  onSMS: (phone: string) => void;
  onNewContact: () => void;
}
 
export function ContactsDirectory({ onCall, onSMS, onNewContact }: ContactsDirectoryProps) {
  const { language } = useLanguage();
  const t = COMM_HUB_T[language];
  const [contacts, setContacts] = useState<MarketingContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingContact, setEditingContact] = useState<MarketingContact | null>(null);
  const [deletingContact, setDeletingContact] = useState<MarketingContact | null>(null);
 
  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'marketingContacts'), orderBy('createdAt', 'desc'), limit(200))
      );
      setContacts(snap.docs.map(d => normalizeContact(d.id, d.data())));
    } catch (err) {
      console.error('Error loading contacts:', err);
    } finally {
      setLoading(false);
    }
  }, []);
 
  useEffect(() => { loadContacts(); }, [loadContacts]);
 
  const filtered = contacts.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) ||
      c.phone?.includes(q) || c.company_name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) || c.role?.toLowerCase().includes(q);
  });
 
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-white/50" />
          <h2 className="text-sm font-semibold text-white">{t.contactBook}</h2>
          <Badge variant="secondary" className="bg-white/10 text-white/50 text-[10px]">{contacts.length}</Badge>
        </div>
        <Button size="sm" onClick={onNewContact} className="gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-8">
          <UserPlus className="w-3.5 h-3.5" />{t.newContactBtn}
        </Button>
      </div>
 
      <div className="px-4 py-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
          <Input placeholder={t.searchContacts} value={search} onChange={e => setSearch(e.target.value)}
            className="pl-8 h-9 text-xs bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-white/20" />
        </div>
      </div>
 
      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center p-12"><Loader2 className="w-5 h-5 animate-spin text-white/30" /></div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-white/30">
            <User className="w-8 h-8 mb-2" />
            <p className="text-xs">{search ? t.noResults : t.noContacts}</p>
          </div>
        ) : (
          <div className="px-4 space-y-1 pb-4">
            {filtered.map(contact => (
              <div key={contact.id} className="group p-3 rounded-xl hover:bg-white/5 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <User className="w-4 h-4 text-white/40" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">{contact.first_name} {contact.last_name}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {contact.company_name && <span className="text-[11px] text-white/40 flex items-center gap-1"><Building2 className="w-3 h-3" /> {contact.company_name}</span>}
                        {contact.role && <span className="text-[11px] text-white/30 flex items-center gap-1"><Briefcase className="w-3 h-3" /> {contact.role}</span>}
                      </div>
                      {contact.phone && <p className="text-[11px] text-white/40 mt-0.5 font-mono">{contact.phone}</p>}
                      {contact.email && <p className="text-[11px] text-white/30 mt-0.5 flex items-center gap-1"><Mail className="w-3 h-3" /> {contact.email}</p>}
                    </div>
                  </div>
                  <Badge className={`text-[9px] ${STATUS_COLORS[contact.conversion_status] || 'bg-white/10 text-white/40'}`}>
                    {getStatusLabel(contact.conversion_status, t)}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity ml-12">
                  {contact.phone && (
                    <>
                      <button onClick={() => onCall(contact.phone!)} className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-500/10 text-emerald-400 text-[11px] hover:bg-emerald-500/20 transition-colors">
                        <Phone className="w-3 h-3" /> {t.callBtn}
                      </button>
                      <button onClick={() => onSMS(contact.phone!)} className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-blue-500/10 text-blue-400 text-[11px] hover:bg-blue-500/20 transition-colors">
                        <MessageSquare className="w-3 h-3" /> {t.smsBtn}
                      </button>
                    </>
                  )}
                  <button onClick={() => setEditingContact(contact)} className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/5 text-white/50 text-[11px] hover:bg-white/10 hover:text-white/70 transition-colors">
                    <Pencil className="w-3 h-3" /> {t.editContact}
                  </button>
                  <button onClick={() => setDeletingContact(contact)} className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-red-500/10 text-red-400 text-[11px] hover:bg-red-500/20 transition-colors">
                    <Trash2 className="w-3 h-3" /> {t.deleteContact || 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
 
      {editingContact && (
        <EditContactDialog contact={editingContact} t={t} onClose={() => setEditingContact(null)}
          onSaved={() => { setEditingContact(null); loadContacts(); }} />
      )}
 
      <AlertDialog open={!!deletingContact} onOpenChange={(open) => { if (!open) setDeletingContact(null); }}>
        <AlertDialogContent className="bg-[hsl(220,20%,13%)] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{t.deleteContact || 'Delete contact'}</AlertDialogTitle>
            <AlertDialogDescription className="text-white/50">
              {t.deleteConfirm || 'Are you sure you want to delete'} <strong className="text-white">{deletingContact?.first_name} {deletingContact?.last_name}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/10 border-white/10 text-white hover:bg-white/20 hover:text-white">{t.cancel}</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-500 text-white"
              onClick={async () => {
                if (!deletingContact) return;
                try {
                  await deleteDoc(doc(db, 'marketingContacts', deletingContact.id));
                  toast.success(t.contactDeleted || 'Contact deleted');
                  loadContacts();
                } catch {
                  toast.error(t.deleteError || 'Error deleting contact');
                }
                setDeletingContact(null);
              }}>
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />{t.deleteContact || 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
 
function EditContactDialog({ contact, t, onClose, onSaved }: {
  contact: MarketingContact; t: Record<string, string>; onClose: () => void; onSaved: () => void;
}) {
  const [firstName, setFirstName] = useState(contact.first_name);
  const [lastName, setLastName] = useState(contact.last_name);
  const [email, setEmail] = useState(contact.email || '');
  const [phone, setPhone] = useState(contact.phone || '');
  const [companyName, setCompanyName] = useState(contact.company_name || '');
  const [role, setRole] = useState(contact.role || '');
  const [specialty, setSpecialty] = useState(contact.specialty || '');
  const [conversionStatus, setConversionStatus] = useState(contact.conversion_status);
  const [saving, setSaving] = useState(false);
 
  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'marketingContacts', contact.id), {
        firstName, lastName,
        email: email || null, phone: phone || null,
        companyName: companyName || null, role,
        specialty: specialty || null,
        conversionStatus,
        updatedAt: serverTimestamp(),
      });
      toast.success(t.contactUpdated);
      onSaved();
    } catch {
      toast.error(t.updateError);
    } finally {
      setSaving(false);
    }
  };
 
  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="bg-[hsl(220,20%,13%)] border-white/10 text-white max-w-md">
        <DialogHeader><DialogTitle className="text-white">{t.editContact}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-white/50">{t.firstName}</Label><Input value={firstName} onChange={e => setFirstName(e.target.value)} className="mt-1 bg-white/5 border-white/10 text-white text-sm" /></div>
            <div><Label className="text-xs text-white/50">{t.lastName}</Label><Input value={lastName} onChange={e => setLastName(e.target.value)} className="mt-1 bg-white/5 border-white/10 text-white text-sm" /></div>
          </div>
          <div><Label className="text-xs text-white/50">{t.email}</Label><Input value={email} onChange={e => setEmail(e.target.value)} className="mt-1 bg-white/5 border-white/10 text-white text-sm" /></div>
          <div><Label className="text-xs text-white/50">{t.phone}</Label><Input value={phone} onChange={e => setPhone(e.target.value)} className="mt-1 bg-white/5 border-white/10 text-white text-sm" /></div>
          <div><Label className="text-xs text-white/50">{t.company}</Label><Input value={companyName} onChange={e => setCompanyName(e.target.value)} className="mt-1 bg-white/5 border-white/10 text-white text-sm" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-white/50">{t.role}</Label><Input value={role} onChange={e => setRole(e.target.value)} className="mt-1 bg-white/5 border-white/10 text-white text-sm" /></div>
            <div><Label className="text-xs text-white/50">{t.specialty}</Label><Input value={specialty} onChange={e => setSpecialty(e.target.value)} className="mt-1 bg-white/5 border-white/10 text-white text-sm" /></div>
          </div>
          <div>
            <Label className="text-xs text-white/50">{t.conversionStatus}</Label>
            <Select value={conversionStatus} onValueChange={setConversionStatus}>
              <SelectTrigger className="mt-1 bg-white/5 border-white/10 text-white text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[hsl(220,20%,15%)] border-white/10">
                {CONVERSION_STATUSES.map(s => <SelectItem key={s} value={s} className="text-white text-sm hover:bg-white/10">{getStatusLabel(s, t)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose} className="text-white/50 hover:text-white">{t.cancel}</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              {saving ? t.saving : t.save}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
 